import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FaChevronDown,
  FaChevronRight,
  FaEdit,
  FaFileAlt,
  FaRegCalendarAlt,
} from 'react-icons/fa';
import { FaCopy, FaUser } from 'react-icons/fa6';
import { FiAlertCircle, FiEye, FiLoader, FiPlus } from 'react-icons/fi';
import { Icon } from '@ohif/ui';

import DeliveryFileModal from './deliveryFile.modal';
import { usePatientDossier } from '../../../helpers/api/patients/patients';
import {
  useCreateStudyInit,
  usePatientPregnancyHistory,
} from '../../../helpers/api/studies/studies';
import { usePermissions } from '../../../helpers/acl/permissions';
import PregnancyModal from '../../ComponnentsUI/WorklistSearch/PregnancyModal';
import ClosePregnancyModal, {
  type ClosingStatus,
} from '../../ComponnentsUI/WorklistSearch/ClosePregnancyModal';
import {
  useCreatePregnancy,
  type PregnancyData,
} from '../../../helpers/api/pregnancy/pregnancy';
import type {
  EnrichedWorklistItem,
  PregnancyResolution,
} from '../../../helpers/api/dicom/dicom';
import { buildPrenatalReportViewerUrl } from '../../ComponnentsUI/features/PrenatalExam/routing';
import { Button } from '../../ComponnentsUI/common/Button';
import {
  ActionMenu,
  ActionMenuItem,
} from '../../ComponnentsUI/common/ActionMenu';
import PatientDetailsView, {
  asNumber,
  derivePrenatalStudyCategory,
  isPregnancyActiveForExam,
  type PatientDossierPayload,
  type PregnancySnapshot,
  unwrapPatientDossier,
} from './PatientDetailsView';

type PatientRecord = Record<string, unknown>;

interface PregnancyHistorySeries {
  serie_instance_uid: string;
  type?: string | null;
}

interface PregnancyHistoryUser {
  first_name?: string | null;
  last_name?: string | null;
}

interface PregnancyHistoryStudy {
  id: string;
  category?: string | null;
  date?: string | null;
  user?: PregnancyHistoryUser | null;
  series?: PregnancyHistorySeries[] | null;
}

interface PregnancyEpisode {
  id: string;
  year?: string;
  status?: string;
  gestationalAge?: string;
  lastMenstrualPeriod?: string | null;
  estimatedDueDate?: string | null;
  closedAt?: string | null;
  outcomeDate?: string | null;
  studies?: PregnancyHistoryStudy[] | null;
}

interface CurrentPregnancySummary {
  gestationalAge?: string;
  edd?: string;
}

interface ObstetricSummary {
  pregnancies: number;
  deliveries: number;
  studies: number;
  anomalies: number;
}

interface PregnancyInfoRecord extends Record<string, unknown> {
  name?: string;
  age?: number;
  phone_number?: string;
  pregnancyHistory?: PregnancyEpisode[];
  currentPregnancy?: CurrentPregnancySummary | null;
  obstetricSummary?: Partial<ObstetricSummary>;
}

function formatGestationalAgeForDisplay(ga: string | null | undefined): string {
  if (ga == null || ga === '' || ga === '—') {
    return '—';
  }
  const match = /^(\d+)\s*\+\s*(\d+)$/.exec(String(ga).trim());
  if (!match) {
    return String(ga);
  }
  const weeks = parseInt(match[1], 10);
  const days = parseInt(match[2], 10);
  if (!Number.isFinite(weeks) || weeks < 0 || weeks > 42) {
    return '—';
  }
  const normalizedDays = Number.isFinite(days)
    ? Math.min(Math.max(days, 0), 6)
    : 0;
  return `${weeks}+${normalizedDays}`;
}

function formatTimelineDate(date: string | null | undefined): string | null {
  if (!date) {
    return null;
  }
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : null;
}

function formatDateForApiDisplay(
  date: string | null | undefined
): string | null {
  if (!date) {
    return null;
  }
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

function getEpisodeLmp(episode: PregnancyEpisode): string | null {
  return episode.lastMenstrualPeriod?.trim() || null;
}

function getEpisodeEstimatedDueDate(episode: PregnancyEpisode): string | null {
  return episode.estimatedDueDate?.trim() || null;
}

function getEpisodeClosedAt(episode: PregnancyEpisode): string | null {
  return episode.closedAt?.trim() || null;
}

function getEpisodeOutcomeDate(episode: PregnancyEpisode): string | null {
  return episode.outcomeDate?.trim() || null;
}

function isEpisodeActive(episode: PregnancyEpisode): boolean {
  return (
    String(episode.status ?? '')
      .trim()
      .toLowerCase() === 'active'
  );
}

function isEpisodePastDue(episode: PregnancyEpisode): boolean {
  if (!isEpisodeActive(episode)) {
    return false;
  }
  const edd = getEpisodeEstimatedDueDate(episode);
  if (!edd) {
    return false;
  }
  const parsed = dayjs(edd);
  return (
    parsed.isValid() && dayjs().startOf('day').isAfter(parsed.startOf('day'))
  );
}

function getEpisodeClosingDate(episode: PregnancyEpisode): string | null {
  if (isEpisodeActive(episode)) {
    return null;
  }
  return getEpisodeOutcomeDate(episode) ?? getEpisodeClosedAt(episode);
}

function calculateGestationalAgeFromLmp(
  lmp: string | null | undefined
): string | undefined {
  if (!lmp) {
    return undefined;
  }
  const parsed = dayjs(lmp);
  if (!parsed.isValid()) {
    return undefined;
  }
  const days = dayjs().startOf('day').diff(parsed.startOf('day'), 'day');
  if (days < 0) {
    return undefined;
  }
  return `${Math.floor(days / 7)}+${days % 7}`;
}

function getEpisodeGestationalAge(
  episode: PregnancyEpisode
): string | undefined {
  return (
    episode.gestationalAge ??
    calculateGestationalAgeFromLmp(getEpisodeLmp(episode))
  );
}

function normalizePregnancyEpisode(
  rawEpisode: unknown
): PregnancyEpisode | null {
  if (rawEpisode == null || typeof rawEpisode !== 'object') {
    return null;
  }

  const episode = rawEpisode as Record<string, unknown>;
  const pickString = (...keys: string[]): string | null => {
    for (const key of keys) {
      const value = episode[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
      if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
      }
    }
    return null;
  };

  const id = pickString('id');
  if (!id) {
    return null;
  }

  const studies = episode.studies;
  return {
    id,
    year: pickString('year') ?? undefined,
    status: pickString('status', 'pregnancy_status') ?? undefined,
    gestationalAge: pickString('gestationalAge', 'gestational_age') ?? undefined,
    lastMenstrualPeriod: pickString(
      'lastMenstrualPeriod',
      'last_menstrual_period'
    ),
    estimatedDueDate: pickString('estimatedDueDate', 'estimated_due_date'),
    closedAt: pickString('closedAt', 'closed_at'),
    outcomeDate: pickString('outcomeDate', 'outcome_date'),
    studies: Array.isArray(studies)
      ? (studies as PregnancyHistoryStudy[])
      : [],
  };
}

function normalizePregnancyHistoryPayload(
  raw: unknown
): Record<string, unknown> | undefined {
  let parsed: unknown = raw;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return undefined;
    }
  }
  if (parsed == null || typeof parsed !== 'object') {
    return undefined;
  }
  const record = parsed as Record<string, unknown>;

  const looksLikePayload = (
    value: unknown
  ): value is Record<string, unknown> => {
    if (value == null || typeof value !== 'object') {
      return false;
    }
    const candidate = value as Record<string, unknown>;
    return (
      'pregnancyHistory' in candidate ||
      'pregnancy_history' in candidate ||
      candidate.currentPregnancy !== undefined ||
      candidate.current_pregnancy !== undefined ||
      candidate.obstetricSummary !== undefined ||
      candidate.obstetric_summary !== undefined ||
      typeof candidate.name === 'string'
    );
  };

  const pick = (payload: Record<string, unknown>): Record<string, unknown> => {
    const history = payload.pregnancyHistory ?? payload.pregnancy_history;
    const current = payload.currentPregnancy ?? payload.current_pregnancy;
    const summary = payload.obstetricSummary ?? payload.obstetric_summary;
    return {
      ...payload,
      pregnancyHistory: Array.isArray(history) ? history : [],
      ...(current !== undefined ? { currentPregnancy: current } : {}),
      ...(summary !== undefined ? { obstetricSummary: summary } : {}),
    };
  };

  const firstData = record.data;
  if (looksLikePayload(firstData)) {
    return pick(firstData as Record<string, unknown>);
  }

  if (looksLikePayload(record)) {
    return pick(record);
  }

  if (firstData != null && typeof firstData === 'object') {
    const secondData = (firstData as Record<string, unknown>).data;
    if (looksLikePayload(secondData)) {
      return pick(secondData as Record<string, unknown>);
    }
  }

  if (firstData != null && typeof firstData === 'object') {
    return pick(firstData as Record<string, unknown>);
  }

  return undefined;
}

function mergePregnancyProfile(
  rawHistoryApi: unknown,
  patientRecord: PatientRecord | undefined
): PregnancyInfoRecord | undefined {
  const base = normalizePregnancyHistoryPayload(rawHistoryApi);
  const embeddedPregnancy = patientRecord?.pregnancy as
    | Record<string, unknown>
    | null
    | undefined;

  const nameFromPatient = patientRecord
    ? `${String(patientRecord.patient_name ?? '').trim()} ${String(
        patientRecord.patient_last_name ?? ''
      ).trim()}`.trim()
    : '';

  let ageFromPatient: number | undefined;
  if (patientRecord?.birth_date) {
    const birthDate = dayjs(patientRecord.birth_date as string);
    if (birthDate.isValid()) {
      ageFromPatient = dayjs().diff(birthDate, 'year');
    }
  }

  const apiHistory = (base?.pregnancyHistory as unknown[]) ?? [];
  let pregnancyHistory = Array.isArray(apiHistory)
    ? apiHistory.flatMap(episode => {
        const normalized = normalizePregnancyEpisode(episode);
        return normalized ? [normalized] : [];
      })
    : [];

  if (
    pregnancyHistory.length === 0 &&
    embeddedPregnancy &&
    embeddedPregnancy.id != null
  ) {
    pregnancyHistory = [
      {
        id: String(embeddedPregnancy.id),
        year: embeddedPregnancy.created_at
          ? dayjs(String(embeddedPregnancy.created_at)).format('YYYY')
          : dayjs().format('YYYY'),
        status: String(embeddedPregnancy.pregnancy_status ?? 'Active'),
        lastMenstrualPeriod:
          typeof embeddedPregnancy.last_menstrual_period === 'string'
            ? embeddedPregnancy.last_menstrual_period
            : null,
        estimatedDueDate:
          typeof embeddedPregnancy.estimated_due_date === 'string'
            ? embeddedPregnancy.estimated_due_date
            : null,
        closedAt:
          typeof embeddedPregnancy.closed_at === 'string'
            ? embeddedPregnancy.closed_at
            : null,
        outcomeDate:
          typeof embeddedPregnancy.outcome_date === 'string'
            ? embeddedPregnancy.outcome_date
            : null,
        studies: [],
      },
    ];
  }

  const currentEpisode = pregnancyHistory.find(
    episode =>
      isEpisodeActive(episode as PregnancyEpisode) &&
      !isEpisodePastDue(episode as PregnancyEpisode)
  ) as PregnancyEpisode | undefined;
  const currentPregnancy: CurrentPregnancySummary | null = currentEpisode
    ? {
        gestationalAge:
          formatGestationalAgeForDisplay(
            getEpisodeGestationalAge(currentEpisode)
          ) || '—',
        edd:
          formatDateForApiDisplay(getEpisodeEstimatedDueDate(currentEpisode)) ??
          '—',
      }
    : null;

  const obstetricSummary = (base?.obstetricSummary ?? {}) as Record<
    string,
    unknown
  >;
  const pregnanciesCount =
    typeof obstetricSummary.pregnancies === 'number'
      ? obstetricSummary.pregnancies
      : Math.max(
          pregnancyHistory.length,
          embeddedPregnancy?.id != null ? 1 : 0
        );

  if (!base && !patientRecord) {
    return undefined;
  }
  if (!base && !embeddedPregnancy && !nameFromPatient) {
    return undefined;
  }

  return {
    ...(base ?? {}),
    name: (base?.name as string) || nameFromPatient || undefined,
    age: (base?.age as number) ?? ageFromPatient,
    phone_number:
      (base?.phone_number as string | undefined) ??
      (patientRecord?.phone_number as string | undefined),
    pregnancyHistory: pregnancyHistory as PregnancyEpisode[],
    currentPregnancy,
    obstetricSummary: {
      pregnancies: pregnanciesCount,
      deliveries: Number(obstetricSummary.deliveries ?? 0),
      studies: Number(obstetricSummary.studies ?? 0),
      anomalies: Number(obstetricSummary.anomalies ?? 0),
    },
  };
}

type PregnancyRecordCtaVariant = 'add' | 'update' | 'close';

function PregnancyRecordCallToAction({
  onClick,
  variant = 'add',
}: {
  onClick: () => void;
  variant?: PregnancyRecordCtaVariant;
}) {
  const { t: tPM } = useTranslation('PatientManagement');
  const label =
    variant === 'add'
      ? tPM('timeline.actions.addPregnancy')
      : variant === 'update'
        ? tPM('timeline.actions.updatePregnancy')
        : tPM('timeline.actions.closePregnancy');

  const sharedFocus =
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-dui/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900';

  const iconBadge =
    variant === 'add' ? (
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-dui text-white shadow-sm"
        aria-hidden
      >
        <FiPlus className="h-4 w-4" strokeWidth={2.25} />
      </span>
    ) : variant === 'update' ? (
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-dui text-white shadow-sm"
        aria-hidden
      >
        <FaEdit className="h-3.5 w-3.5" />
      </span>
    ) : (
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-500 text-white shadow-sm dark:bg-slate-600"
        aria-hidden
      >
        <FiAlertCircle className="h-4 w-4" strokeWidth={2.25} />
      </span>
    );

  const buttonClass =
    variant === 'close'
      ? `flex w-full items-center justify-center gap-2.5 rounded-lg border border-slate-200 bg-card-color-200 px-3 py-2.5 text-sm font-medium text-title-page-color-100 transition-colors hover:bg-card-color-100 dark:border-slate-600 dark:bg-gray-900/50 dark:text-gray-200 dark:hover:bg-gray-800/70 ${sharedFocus}`
      : `flex w-full items-center justify-center gap-2.5 rounded-lg border-0 bg-primary-dui/10 px-3 py-2.5 text-sm font-medium text-primary-dui transition-colors hover:bg-primary-dui/15 dark:bg-primary-dui/20 dark:text-primary-light dark:hover:bg-primary-dui/25 ${sharedFocus}`;

  return (
    <button type="button" onClick={onClick} className={buttonClass}>
      {iconBadge}
      {label}
    </button>
  );
}

const PatientTimeline = () => {
  const { t: tP } = useTranslation('Patient');
  const { t: tPM } = useTranslation('PatientManagement');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: createStudyInit } = useCreateStudyInit(
    undefined,
    undefined,
    false
  );

  const {
    data: patientPregnancyHistory,
    error: errorPatientPregnancyHistory,
    isLoading: isLoadingPatientPregnancyHistory,
    refetch: refetchPatientPregnancyHistory,
  } = usePatientPregnancyHistory(id);
  const {
    data: dossierResponse,
    error: errorPatient,
    isLoading,
    refetch,
  } = usePatientDossier(id);

  const dossier: PatientDossierPayload = useMemo(
    () => unwrapPatientDossier(dossierResponse),
    [dossierResponse]
  );
  const patientData = useMemo(() => {
    if (!dossier.patient) {
      return undefined;
    }
    const merged: PatientRecord = {
      ...(dossier.patient as PatientRecord),
    };
    if (dossier.pregnancy && merged.pregnancy == null) {
      merged.pregnancy = dossier.pregnancy as PatientRecord;
    }
    return { data: merged } as { data: PatientRecord };
  }, [dossier]);
  const patientRecord = patientData?.data;
  const activePregnancy =
    (patientRecord?.pregnancy as PregnancySnapshot | null | undefined) ?? null;

  const [openDeliveryFileModal, setOpenDeliveryFileModal] = useState(false);
  const [pregnancyRecordModalOpen, setPregnancyRecordModalOpen] =
    useState(false);
  const [pregnancyModalMode, setPregnancyModalMode] = useState<
    'create' | 'edit-backend'
  >('create');
  const [pregnancyModalInitialData, setPregnancyModalInitialData] = useState<
    | Partial<{
        lmp: string;
        edd: string;
        gaWeeks: string;
        gaDays: string;
        crl: string;
        bpd: string;
        hc: string;
        ac: string;
        fl: string;
        grossesse_classee_a_risque: boolean | null;
        risque_anemie: boolean;
        risque_hta: boolean;
        risque_diabete: boolean;
        risque_cardiopathie: boolean;
        risque_metrorragie: boolean;
        risque_infection: boolean;
        risque_preeclampsie: boolean;
        risque_eclampsie: boolean;
        risque_autre_details: string;
        status:
          | 'Active'
          | 'Delivered'
          | 'Miscarried'
          | 'Terminated'
          | 'Unknown';
        trimester: 'T1' | 'T2' | 'T3';
      }>
    | undefined
  >(undefined);
  const [editingPregnancyId, setEditingPregnancyId] = useState<
    number | undefined
  >(undefined);
  const [closePregnancyModalOpen, setClosePregnancyModalOpen] = useState(false);
  const [closePregnancyInitialStatus, setClosePregnancyInitialStatus] =
    useState<ClosingStatus | undefined>(undefined);
  const [expandedPregnancies, setExpandedPregnancies] = useState<
    Record<string, boolean>
  >({});
  const [expandedStudies, setExpandedStudies] = useState<
    Record<string, boolean>
  >({});
  const [isPregnancyHistoryExpanded, setIsPregnancyHistoryExpanded] =
    useState(false);
  const [isCreatingExam, setIsCreatingExam] = useState(false);

  const pregnancyInfo = useMemo(
    () => mergePregnancyProfile(patientPregnancyHistory, patientRecord),
    [patientPregnancyHistory, patientRecord]
  );

  const pregnancyHistoryList = useMemo<PregnancyEpisode[]>(() => {
    const rawHistory = pregnancyInfo?.pregnancyHistory;
    return Array.isArray(rawHistory) ? rawHistory : [];
  }, [pregnancyInfo?.pregnancyHistory]);

  const currentPregnancyPastDue = useMemo(() => {
    const pastDueEpisode = pregnancyHistoryList.find(isEpisodePastDue);
    const edd = pastDueEpisode
      ? getEpisodeEstimatedDueDate(pastDueEpisode)
      : null;
    if (!edd) {
      return null;
    }
    return formatTimelineDate(edd);
  }, [pregnancyHistoryList]);

  const patientName = useMemo(() => {
    const fromPregnancy = String(pregnancyInfo?.name ?? '').trim();
    if (fromPregnancy) {
      return fromPregnancy;
    }
    const fallback = tPM('timeline.sidebar.fallbackName');
    if (!patientRecord) {
      return fallback;
    }
    const first = String(patientRecord.patient_name ?? '').trim();
    const last = String(patientRecord.patient_last_name ?? '').trim();
    const combined = `${first} ${last}`.trim();
    return combined || fallback;
  }, [patientRecord, pregnancyInfo?.name, tPM]);

  const patientBirthDateLabel = useMemo(() => {
    const raw = patientRecord?.birth_date;
    if (raw == null || String(raw).trim() === '') {
      return null;
    }
    const parsed = dayjs(raw as string);
    return parsed.isValid() ? parsed.format('MMM D, YYYY') : null;
  }, [patientRecord]);

  const patientMeta = useMemo(() => {
    const items: string[] = [];
    if (pregnancyInfo?.age != null) {
      items.push(
        tPM('timeline.sidebar.ageYears', {
          count: Number(pregnancyInfo.age),
        })
      );
    } else {
      items.push(tPM('timeline.sidebar.ageNotProvided'));
    }

    items.push(
      patientBirthDateLabel
        ? tPM('timeline.sidebar.dobPrefix', { date: patientBirthDateLabel })
        : tPM('timeline.sidebar.birthDateNotProvided')
    );

    const phone =
      (pregnancyInfo?.phone_number as string | undefined) ??
      (patientRecord?.phone_number as string | undefined);
    if (phone) {
      items.push(`${tP('dossier_phone')}: ${phone}`);
    }

    return items;
  }, [
    patientBirthDateLabel,
    patientRecord,
    pregnancyInfo?.age,
    pregnancyInfo?.phone_number,
    tP,
    tPM,
  ]);

  // Write access = the ACL patient.write permission (backend enforces record reach).
  // Replaces the legacy per-patient `permissions` (SharedPatient) check.
  const { can } = usePermissions();
  const hasWritePermission = can('patient.write');

  const canAddPregnancy =
    hasWritePermission && Boolean(id) && !pregnancyInfo?.currentPregnancy;

  const canUpdatePregnancy =
    hasWritePermission &&
    Boolean(id) &&
    Boolean(
      (patientRecord as { pregnancy?: { id?: number } } | undefined)?.pregnancy
        ?.id
    );

  const activePregnancyId = useMemo<number | null>(() => {
    if (!activePregnancy || !isPregnancyActiveForExam(activePregnancy)) {
      return null;
    }
    return asNumber(activePregnancy.id);
  }, [activePregnancy]);

  const canClosePregnancy =
    hasWritePermission && Boolean(id) && activePregnancyId !== null;

  const canCreateHeaderExam =
    hasWritePermission && Boolean(patientRecord) && activePregnancyId !== null;

  const handleCreateExam = async () => {
    if (isCreatingExam) {
      return;
    }

    if (
      !activePregnancy ||
      !activePregnancyId ||
      !isPregnancyActiveForExam(activePregnancy)
    ) {
      message.error(tP('prenatalExam.messages.inactivePregnancy'));
      return;
    }

    setIsCreatingExam(true);

    try {
      const response = await createStudyInit({
        pregnancy_id: activePregnancyId,
        study_mode: 'UPLOADER',
        category: derivePrenatalStudyCategory(activePregnancy),
        type: 'OB',
        study_date: new Date().toISOString(),
        skip_serie_creation: true,
      });

      const studyInstanceUid = response?.data?.result?.study_instance_uid;
      if (!studyInstanceUid) {
        throw new Error('Study creation did not return a study_instance_uid.');
      }

      void queryClient.invalidateQueries({ queryKey: ['pregnancy'] });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
      void queryClient.invalidateQueries({ queryKey: ['recent-studies'] });
      void queryClient.invalidateQueries({ queryKey: ['paginated-exams'] });
      void queryClient.invalidateQueries({ queryKey: ['studies'] });

      navigate(buildPrenatalReportViewerUrl(studyInstanceUid));
    } catch (error) {
      console.error('Failed to create prenatal exam study:', error);
      message.error(tP('prenatalExam.messages.createError'));
    } finally {
      setIsCreatingExam(false);
    }
  };

  const togglePregnancyExpansion = (episodeId: string) => {
    setExpandedPregnancies((prev) => ({
      ...prev,
      [episodeId]: !prev[episodeId],
    }));
  };

  const toggleStudyExpansion = (studyId: string) => {
    setExpandedStudies((prev) => ({
      ...prev,
      [studyId]: !prev[studyId],
    }));
  };

  const openEditPatientPage = (patientId: string | number | undefined) => {
    if (patientId == null) {
      return;
    }
    navigate(`/patients/${patientId}/edit`, {
      state: { returnTo: `/patients/${patientId}` },
    });
  };

  const closePregnancyRecordModal = () => {
    setPregnancyRecordModalOpen(false);
    setPregnancyModalInitialData(undefined);
    setEditingPregnancyId(undefined);
    setPregnancyModalMode('create');
  };

  const openClosePregnancyModal = (initial?: ClosingStatus) => {
    if (!activePregnancyId) {
      return;
    }
    setClosePregnancyInitialStatus(initial);
    setClosePregnancyModalOpen(true);
  };

  const handleCloseClosePregnancyModal = () => {
    setClosePregnancyModalOpen(false);
    setClosePregnancyInitialStatus(undefined);
  };

  const handleRequestClosePregnancy = (status: string) => {
    closePregnancyRecordModal();
    if (
      status === 'Delivered' ||
      status === 'Terminated' ||
      status === 'Miscarried' ||
      status === 'Unknown'
    ) {
      openClosePregnancyModal(status as ClosingStatus);
    }
  };

  const handlePregnancyClosed = () => {
    void refetchPatientPregnancyHistory();
    void refetch();
  };

  const openPregnancyModalCreate = () => {
    setPregnancyModalMode('create');
    setPregnancyModalInitialData(undefined);
    setEditingPregnancyId(undefined);
    setPregnancyRecordModalOpen(true);
  };

  const openPregnancyModalUpdate = () => {
    const pregnancy = (
      patientRecord as
        | {
            pregnancy?: {
              id: number;
              last_menstrual_period?: string | null;
              estimated_due_date?: string | null;
              ultrasound_date?: string | null;
              closed_at?: string | null;
              outcome_date?: string | null;
              preg_bpd?: number | null;
              preg_hc?: number | null;
              preg_ac?: number | null;
              preg_fl?: number | null;
              preg_crl?: number | null;
              pregnancy_status?: string | null;
              grossesse_classee_a_risque?: boolean | null;
              risque_anemie?: boolean;
              risque_hta?: boolean;
              risque_diabete?: boolean;
              risque_cardiopathie?: boolean;
              risque_metrorragie?: boolean;
              risque_infection?: boolean;
              risque_preeclampsie?: boolean;
              risque_eclampsie?: boolean;
              risque_autre_details?: string | null;
            };
          }
        | undefined
    )?.pregnancy;
    if (!pregnancy?.id) {
      return;
    }

    const lmp = pregnancy.last_menstrual_period
      ? dayjs(pregnancy.last_menstrual_period).format('YYYY-MM-DD')
      : '';

    const gestationalAge = calculateGestationalAgeFromLmp(lmp);
    const gaMatch = /^(\d+)\+(\d+)$/.exec(gestationalAge ?? '');
    const gaWeeks = gaMatch ? Number(gaMatch[1]) : null;
    const gaDays = gaMatch ? Number(gaMatch[2]) : null;

    setPregnancyModalMode('edit-backend');
    setPregnancyModalInitialData({
      lmp,
      edd: pregnancy.estimated_due_date
        ? dayjs(pregnancy.estimated_due_date).format('YYYY-MM-DD')
        : '',
      gaWeeks: gaWeeks != null && gaWeeks > 0 ? String(gaWeeks) : '',
      gaDays: gaDays != null && gaDays > 0 ? String(gaDays) : '',
      crl: pregnancy.preg_crl != null ? String(pregnancy.preg_crl) : '',
      bpd: pregnancy.preg_bpd != null ? String(pregnancy.preg_bpd) : '',
      hc: pregnancy.preg_hc != null ? String(pregnancy.preg_hc) : '',
      ac: pregnancy.preg_ac != null ? String(pregnancy.preg_ac) : '',
      fl: pregnancy.preg_fl != null ? String(pregnancy.preg_fl) : '',
      grossesse_classee_a_risque:
        pregnancy.grossesse_classee_a_risque ?? null,
      risque_anemie: pregnancy.risque_anemie === true,
      risque_hta: pregnancy.risque_hta === true,
      risque_diabete: pregnancy.risque_diabete === true,
      risque_cardiopathie: pregnancy.risque_cardiopathie === true,
      risque_metrorragie: pregnancy.risque_metrorragie === true,
      risque_infection: pregnancy.risque_infection === true,
      risque_preeclampsie: pregnancy.risque_preeclampsie === true,
      risque_eclampsie: pregnancy.risque_eclampsie === true,
      risque_autre_details: pregnancy.risque_autre_details ?? '',
      status: (pregnancy.pregnancy_status || 'Active') as
        | 'Active'
        | 'Delivered'
        | 'Miscarried'
        | 'Terminated'
        | 'Unknown',
      trimester:
        gaWeeks != null && gaWeeks >= 28
          ? 'T3'
          : gaWeeks != null && gaWeeks >= 14
            ? 'T2'
            : 'T1',
    });
    setEditingPregnancyId(pregnancy.id);
    setPregnancyRecordModalOpen(true);
  };

  const { mutate: createPregnancy } = useCreatePregnancy(
    () => {
      void refetchPatientPregnancyHistory();
      void refetch();
      closePregnancyRecordModal();
    },
    () => {
      message.error(tPM('timeline.errors.createPregnancy'));
    }
  );

  const pregnancyModalWorklistItem =
    useMemo((): EnrichedWorklistItem | null => {
      if (!id || !patientRecord) {
        return null;
      }
      const embeddedPregnancy = patientRecord.pregnancy as
        | Record<string, unknown>
        | null
        | undefined;
      const lastName = String(patientRecord.patient_last_name ?? '').trim();
      const firstName = String(patientRecord.patient_name ?? '').trim();
      const patientNameForMwl =
        lastName || firstName ? `${lastName}^${firstName}` : 'Unknown^Patient';
      const birthRaw = patientRecord.birth_date;
      const birthDate =
        birthRaw != null && birthRaw !== ''
          ? dayjs(birthRaw as string).format('YYYYMMDD')
          : '';
      const activeStatus =
        embeddedPregnancy &&
        String(embeddedPregnancy.pregnancy_status ?? '') === 'Active';

      return {
        mwl: {
          PatientName: patientNameForMwl,
          PatientID: String(id),
          PatientBirthDate: birthDate,
          PatientSex: 'F',
        },
        resolution: {
          patient_exists: true,
          patient_uuid: String(id),
          patient_id_internal: String(patientRecord.patient_id ?? id),
          active_pregnancy_exists: Boolean(activeStatus),
          pregnancy:
            embeddedPregnancy && embeddedPregnancy.id != null
              ? (embeddedPregnancy as unknown as PregnancyResolution)
              : null,
          study_exists: false,
          study_instance_uid: null,
        },
      };
    }, [id, patientRecord]);

  const handleSaveNewPregnancyFromModal = (pregnancyData: {
    lmp: string;
    edd: string;
    bpd: string;
    hc: string;
    ac: string;
    fl: string;
    crl: string;
    status?: string;
    grossesse_classee_a_risque?: boolean | null;
    risque_anemie?: boolean;
    risque_hta?: boolean;
    risque_diabete?: boolean;
    risque_cardiopathie?: boolean;
    risque_metrorragie?: boolean;
    risque_infection?: boolean;
    risque_preeclampsie?: boolean;
    risque_eclampsie?: boolean;
    risque_autre_details?: string;
    trimester: 'T1' | 'T2' | 'T3';
  }) => {
    if (!id) {
      return;
    }
    const payload: PregnancyData = {
      patient_id: String(id),
      dating_method: 'LMP',
      last_menstrual_period: pregnancyData.lmp || undefined,
      estimated_due_date: pregnancyData.edd || undefined,
      preg_bpd: pregnancyData.bpd ? parseFloat(pregnancyData.bpd) : undefined,
      preg_hc: pregnancyData.hc ? parseFloat(pregnancyData.hc) : undefined,
      preg_ac: pregnancyData.ac ? parseFloat(pregnancyData.ac) : undefined,
      preg_fl: pregnancyData.fl ? parseFloat(pregnancyData.fl) : undefined,
      preg_crl: pregnancyData.crl ? parseFloat(pregnancyData.crl) : undefined,
      pregnancy_status: pregnancyData.status || 'Active',
      grossesse_classee_a_risque: pregnancyData.grossesse_classee_a_risque,
      risque_anemie: pregnancyData.risque_anemie === true,
      risque_hta: pregnancyData.risque_hta === true,
      risque_diabete: pregnancyData.risque_diabete === true,
      risque_cardiopathie: pregnancyData.risque_cardiopathie === true,
      risque_metrorragie: pregnancyData.risque_metrorragie === true,
      risque_infection: pregnancyData.risque_infection === true,
      risque_preeclampsie: pregnancyData.risque_preeclampsie === true,
      risque_eclampsie: pregnancyData.risque_eclampsie === true,
      risque_autre_details: pregnancyData.risque_autre_details?.trim() || null,
    };

    if (
      pregnancyData.bpd ||
      pregnancyData.hc ||
      pregnancyData.ac ||
      pregnancyData.fl ||
      pregnancyData.crl
    ) {
      payload.dating_method = 'ULTRASOUND';
    } else if (pregnancyData.edd && !pregnancyData.lmp) {
      payload.dating_method = 'EDD';
    }

    createPregnancy(payload);
  };

  useEffect(() => {
    const statuses = [errorPatientPregnancyHistory, errorPatient]
      .map(
        (error) =>
          (error as { status_code?: number } | undefined)?.status_code ?? null
      )
      .filter((code): code is number => code != null);

    if (statuses.includes(403) || statuses.includes(401)) {
      navigate(-1);
      message.error(tPM('timeline.errors.unauthorized'));
    } else if (statuses.some((code) => code >= 500 && code < 600)) {
      navigate(-1);
      message.error(tPM('timeline.errors.serverError'));
    }
  }, [errorPatient, errorPatientPregnancyHistory, navigate, tPM]);

  const copyToClipboard = (value: string) => {
    void navigator.clipboard.writeText(value).then(() => {
      message.success(tPM('timeline.notifications.uidCopied'));
    });
  };

  const getExamTitle = (category: string | null | undefined) => {
    switch (category) {
      case 'T1':
        return tPM('timeline.exams.firstTrimester');
      case 'T2':
        return tPM('timeline.exams.secondTrimester');
      case 'T3':
        return tPM('timeline.exams.thirdTrimester');
      default:
        return tPM('timeline.exams.generic');
    }
  };

  const pregnancyHistoryContent = (
    <div className="rounded-2xl border border-slate-200/80 bg-card-color-100 shadow-sm dark:border-slate-700 dark:bg-gray-900/30">
      <button
        type="button"
        className={`flex w-full flex-wrap items-start justify-between gap-3 px-4 py-4 text-left transition-colors hover:bg-card-color-200/30 dark:hover:bg-gray-900/30 ${
          isPregnancyHistoryExpanded
            ? 'border-b border-slate-200/80 dark:border-slate-700'
            : ''
        }`}
        onClick={() => setIsPregnancyHistoryExpanded((prev) => !prev)}
        aria-expanded={isPregnancyHistoryExpanded}
      >
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-dui/10 text-primary-dui dark:bg-primary-dui/20 dark:text-primary-light">
            {isPregnancyHistoryExpanded ? (
              <FaChevronDown className="h-4 w-4" />
            ) : (
              <FaChevronRight className="h-4 w-4" />
            )}
          </span>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-title-page-color-300 dark:text-gray-400">
              {tPM('timeline.history.label')}
            </p>
            <h4 className="mt-1 text-base font-semibold tracking-tight text-title-page-color-100 dark:text-white">
              {tPM('timeline.history.title')}
            </h4>
            <p className="mt-1 text-sm leading-relaxed text-title-page-color-300 dark:text-gray-400">
              {tPM('timeline.history.helper')}
            </p>
          </div>
        </div>
        <span className="rounded-full bg-card-color-200 px-2.5 py-0.5 text-xs font-medium tabular-nums text-title-page-color-300 dark:bg-gray-800 dark:text-gray-300">
          {tPM('timeline.history.episode', {
            count: pregnancyHistoryList.length,
          })}
        </span>
      </button>

      {isPregnancyHistoryExpanded ? (
        !pregnancyHistoryList.length ? (
          <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-card-color-200 text-title-page-color-300 ring-1 ring-slate-200/70 dark:bg-gray-800 dark:text-gray-400 dark:ring-slate-700">
              <FaRegCalendarAlt className="text-xl" />
            </div>
            <p className="mt-4 text-sm font-medium text-title-page-color-100 dark:text-gray-200">
              {tPM('timeline.history.empty')}
            </p>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-title-page-color-300 dark:text-gray-400">
              {tPM('timeline.history.emptyHint')}
            </p>
          </div>
        ) : (
          <div className="relative px-4 py-4">
            <div
              className="absolute bottom-4 left-[29px] top-4 w-px bg-gradient-to-b from-slate-300 via-slate-200 to-transparent dark:from-slate-600 dark:via-slate-700 dark:to-transparent"
              aria-hidden
            />
            <div className="relative space-y-4">
              {pregnancyHistoryList.map((episode, episodeIndex) => {
                const episodeId = String(episode.id);
                const isExpanded = expandedPregnancies[episodeId] ?? true;
                const isActive = isEpisodeActive(episode);
                const closingDateLabel = formatTimelineDate(
                  getEpisodeClosingDate(episode)
                );
                const estimatedDueDateLabel = formatTimelineDate(
                  getEpisodeEstimatedDueDate(episode)
                );
                const episodeGestationalAge =
                  getEpisodeGestationalAge(episode);
                const episodePastDue = isEpisodePastDue(episode);
                const studies = Array.isArray(episode.studies)
                  ? episode.studies
                  : [];

                return (
                  <div key={episodeId} className="relative pl-9">
                    <span
                      className={`absolute left-[11px] top-5 z-[1] h-3 w-3 -translate-x-1/2 rounded-full border-2 border-card-color-100 shadow-md ring-2 ring-slate-200 dark:border-gray-800 dark:ring-slate-700 ${
                        isActive
                          ? 'bg-emerald-500 dark:bg-emerald-500'
                          : 'bg-slate-400 dark:bg-slate-500'
                      }`}
                      aria-hidden
                    />
                    <div className="overflow-hidden rounded-xl border border-slate-200 bg-card-color-100 shadow-sm transition-all duration-200 hover:shadow-md dark:border-slate-700 dark:bg-gray-800 dark:shadow-none dark:hover:border-slate-600">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left sm:px-5 sm:py-4"
                        onClick={() => togglePregnancyExpansion(episodeId)}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="text-title-page-color-300 dark:text-gray-400">
                            {isExpanded ? (
                              <FaChevronDown className="text-xs" />
                            ) : (
                              <FaChevronRight className="text-xs" />
                            )}
                          </span>
                          <div className="min-w-0">
                            <h5 className="truncate text-[15px] font-semibold tracking-tight text-title-page-color-100 dark:text-white">
                              {tPM('timeline.history.episodeTitle', {
                                year: episode.year ?? String(episodeIndex + 1),
                              })}
                            </h5>
                            <p className="mt-0.5 text-sm text-title-page-color-300 dark:text-gray-400">
                              {tPM('timeline.history.gaShort')}{' '}
                              <span
                                className="font-medium text-title-page-color-100 dark:text-gray-200"
                                title={
                                  formatGestationalAgeForDisplay(
                                    episodeGestationalAge
                                  ) === '—'
                                    ? tPM('timeline.history.reviewDating')
                                    : undefined
                                }
                              >
                                {formatGestationalAgeForDisplay(
                                  episodeGestationalAge
                                )}
                              </span>
                            </p>
                            {closingDateLabel ||
                            (episodePastDue && estimatedDueDateLabel) ? (
                              <p className="mt-0.5 text-xs font-medium text-title-page-color-300 dark:text-gray-400">
                                {closingDateLabel
                                  ? `Closed ${closingDateLabel}`
                                  : `Due date passed ${estimatedDueDateLabel}`}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                          {isActive ? (
                            <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
                              <span className="h-1 w-1 rounded-full bg-emerald-500" />
                              {tPM('timeline.history.statusActive')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-md bg-card-color-200 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-title-page-color-300 dark:bg-gray-900/50 dark:text-gray-300">
                              {tPM('timeline.history.statusCompleted')}
                            </span>
                          )}
                          <span className="text-xs font-medium tabular-nums text-title-page-color-300 dark:text-gray-400">
                            {tPM('timeline.history.examsCount', {
                              count: studies.length,
                            })}
                          </span>
                        </div>
                      </button>

                      {isExpanded ? (
                        <div className="space-y-2 border-t border-slate-200 bg-card-color-200/50 px-3 py-3 dark:border-slate-700 dark:bg-gray-900/40 sm:px-4 sm:py-4">
                          {studies.length > 0 ? (
                            studies.map((exam) => {
                              const studyId = String(exam.id);
                              const isStudyExpanded =
                                expandedStudies[studyId] ?? false;
                              const series = Array.isArray(exam.series)
                                ? exam.series
                                : [];
                              const userFirstName = String(
                                exam.user?.first_name ?? ''
                              ).trim();
                              const userLastName = String(
                                exam.user?.last_name ?? ''
                              ).trim();
                              const examOwner =
                                `${userFirstName} ${userLastName}`.trim();

                              return (
                                <div
                                  key={studyId}
                                  className="overflow-hidden rounded-lg border-l-[3px] border-l-primary-dui/60 bg-card-color-100 ring-1 ring-slate-200/70 dark:border-l-primary-dui dark:bg-gray-900/40 dark:ring-slate-700"
                                >
                                  <div className="flex items-center justify-between gap-3 p-3 hover:bg-card-color-200/60 dark:hover:bg-gray-900/50 sm:p-3.5">
                                    <button
                                      type="button"
                                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                                      onClick={() =>
                                        toggleStudyExpansion(studyId)
                                      }
                                    >
                                      {isStudyExpanded ? (
                                        <FaChevronDown className="shrink-0 text-xs text-title-page-color-300 dark:text-gray-400" />
                                      ) : (
                                        <FaChevronRight className="shrink-0 text-xs text-title-page-color-300 dark:text-gray-400" />
                                      )}
                                      <div className="min-w-0">
                                        <p className="text-sm font-semibold text-title-page-color-100 dark:text-gray-100">
                                          {getExamTitle(exam.category)}
                                        </p>
                                        <p className="mt-0.5 text-xs text-title-page-color-300 dark:text-gray-400">
                                          {exam.date ||
                                            tPM('timeline.sidebar.notProvided')}
                                          {examOwner ? ` · ${examOwner}` : ''}
                                        </p>
                                        <p className="mt-1 font-mono text-[11px] text-title-page-color-300 dark:text-gray-500">
                                          {studyId}
                                        </p>
                                      </div>
                                    </button>
                                    <div
                                      className="relative"
                                      onClick={(event) =>
                                        event.stopPropagation()
                                      }
                                    >
                                      <ActionMenu>
                                        <ActionMenuItem
                                          icon={FiEye}
                                          label={tPM(
                                            'timeline.history.viewExam'
                                          )}
                                          as={Link}
                                          to={`/exam/viewer?StudyInstanceUIDs=${studyId}`}
                                        />
                                      </ActionMenu>
                                    </div>
                                  </div>

                                  {isStudyExpanded ? (
                                    <div className="space-y-3 border-t border-slate-200 bg-card-color-200/40 px-3 py-3 dark:border-slate-700 dark:bg-gray-900/30 sm:px-4 sm:py-4">
                                      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-title-page-color-300 dark:text-gray-400">
                                        {tPM('timeline.history.seriesCount', {
                                          count: series.length,
                                        })}
                                      </p>
                                      {series.length > 0 ? (
                                        series.map((seriesItem) => (
                                          <div
                                            key={seriesItem.serie_instance_uid}
                                            className="rounded-lg bg-card-color-100 p-3.5 ring-1 ring-slate-200/60 dark:bg-gray-800/60 dark:ring-slate-700 sm:p-4"
                                          >
                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
                                              <div>
                                                <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-title-page-color-300 dark:text-gray-500">
                                                  {tPM(
                                                    'timeline.history.typeLabel'
                                                  )}
                                                </p>
                                                <p className="text-sm font-semibold text-title-page-color-100 dark:text-gray-200">
                                                  {seriesItem.type ||
                                                    tPM(
                                                      'timeline.history.typeNotAvailable'
                                                    )}
                                                </p>
                                              </div>
                                              <div className="sm:col-span-2">
                                                <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-title-page-color-300 dark:text-gray-500">
                                                  {tPM(
                                                    'timeline.history.seriesUidLabel'
                                                  )}
                                                </p>
                                                <div className="flex items-center justify-between gap-2 rounded-md bg-card-color-200 px-2 py-1.5 ring-1 ring-slate-200/70 dark:bg-gray-900/50 dark:ring-slate-700">
                                                  <p className="truncate font-mono text-[11px] text-title-page-color-300 dark:text-gray-300">
                                                    {
                                                      seriesItem.serie_instance_uid
                                                    }
                                                  </p>
                                                  <FaCopy
                                                    className="h-3.5 w-3.5 shrink-0 cursor-pointer text-title-page-color-300 hover:text-title-page-color-100 dark:text-gray-400 dark:hover:text-gray-200"
                                                    onClick={() =>
                                                      copyToClipboard(
                                                        seriesItem.serie_instance_uid
                                                      )
                                                    }
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))
                                      ) : (
                                        <p className="text-sm text-title-page-color-300 dark:text-gray-400">
                                          {tPM('timeline.history.noSeries')}
                                        </p>
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })
                          ) : (
                            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 py-12 text-center dark:border-slate-600">
                              <p className="text-sm font-medium text-title-page-color-300 dark:text-gray-400">
                                {tPM('timeline.noExamsRecorded', {
                                  defaultValue:
                                    'No exams recorded for this pregnancy episode',
                                })}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )
      ) : null}
    </div>
  );

  const pregnancySupplementalContent = (
    <div className="space-y-4 border-t border-slate-100 pt-4 dark:border-slate-700">
      {currentPregnancyPastDue ? (
        <div
          className="flex gap-2 rounded-xl border border-amber-200/90 bg-amber-50 px-3 py-3 text-sm text-amber-950 dark:border-amber-800/80 dark:bg-amber-950/40 dark:text-amber-100"
          role="status"
        >
          <FiAlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
            aria-hidden
          />
          <div>
            <p className="font-semibold text-amber-900 dark:text-amber-100">
              {tPM('timeline.sidebar.eddPassed')}
            </p>
            <p className="mt-0.5 text-amber-800/90 dark:text-amber-200/90">
              {tPM('timeline.sidebar.eddPassedDescription', {
                date: currentPregnancyPastDue,
              })}
            </p>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4">
        <div className="rounded-2xl border border-slate-200/80 bg-card-color-100 p-4 shadow-sm dark:border-slate-700 dark:bg-gray-900/30">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-title-page-color-300 dark:text-gray-400">
            {tPM('timeline.currentPregnancy.documentation')}
          </p>
          <p className="mt-1 text-sm leading-relaxed text-title-page-color-300 dark:text-gray-400">
            {tPM('timeline.currentPregnancy.documentationHint')}
          </p>
          <div className="mt-4 space-y-2">
            {canAddPregnancy && pregnancyModalWorklistItem ? (
              <PregnancyRecordCallToAction
                variant="add"
                onClick={openPregnancyModalCreate}
              />
            ) : null}
            {canUpdatePregnancy && pregnancyModalWorklistItem ? (
              <PregnancyRecordCallToAction
                variant="update"
                onClick={openPregnancyModalUpdate}
              />
            ) : null}
            {canClosePregnancy ? (
              <PregnancyRecordCallToAction
                variant="close"
                onClick={() => openClosePregnancyModal()}
              />
            ) : null}
            {id ? (
              <Button
                type="button"
                variant="secondary"
                className="w-full justify-center gap-2"
                onClick={() => setOpenDeliveryFileModal(true)}
              >
                <FaFileAlt className="h-4 w-4" />
                {tPM('timeline.currentPregnancy.deliveryFile')}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {pregnancyHistoryContent}
    </div>
  );

  if (isLoading || isLoadingPatientPregnancyHistory) {
    return (
      <div className="fixed inset-0 z-[100] flex h-full w-full flex-col items-center justify-center bg-card-color-100">
        <div className="loader">
          <Icon name={'deepechoLogo2'} />
        </div>
      </div>
    );
  }

  if (errorPatient) {
    return (
      <div className="p-8 text-center text-red-500">
        {tPM('timeline.errors.loadProfile')}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-card-color-200 px-4 py-4 dark:bg-gray-900 sm:px-5 sm:py-5">
      {openDeliveryFileModal && id ? (
        <DeliveryFileModal
          isOpen={openDeliveryFileModal}
          onClose={() => setOpenDeliveryFileModal(false)}
          patientId={id}
          hasWritePermission={hasWritePermission}
        />
      ) : null}
      <PregnancyModal
        isOpen={pregnancyRecordModalOpen && !!pregnancyModalWorklistItem}
        onClose={closePregnancyRecordModal}
        worklistItem={pregnancyModalWorklistItem}
        mode={pregnancyModalMode}
        pregnancyId={editingPregnancyId}
        initialData={pregnancyModalInitialData}
        onSaveLocal={
          pregnancyModalMode === 'create'
            ? handleSaveNewPregnancyFromModal
            : undefined
        }
        onUpdateSuccess={() => {
          void refetchPatientPregnancyHistory();
          void refetch();
        }}
        onRequestClosePregnancy={
          pregnancyModalMode === 'edit-backend' && activePregnancyId
            ? handleRequestClosePregnancy
            : undefined
        }
        ultrasoundDate={(() => {
          const raw = (
            patientRecord as
              | {
                  pregnancy?: { ultrasound_date?: string };
                }
              | undefined
          )?.pregnancy?.ultrasound_date;
          if (!raw) {
            return undefined;
          }
          const parsed = dayjs(raw);
          return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
        })()}
      />
      {id && activePregnancyId ? (
        <ClosePregnancyModal
          isOpen={closePregnancyModalOpen}
          onClose={handleCloseClosePregnancyModal}
          patientId={String(id)}
          pregnancyId={activePregnancyId}
          initialStatus={closePregnancyInitialStatus}
          onClosed={handlePregnancyClosed}
        />
      ) : null}

      <div className="mx-auto flex h-full min-h-0 w-full flex-1 flex-col gap-5">
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-card-color-100 shadow-sm dark:border-slate-700 dark:bg-gray-800">
          <div className="bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.14),transparent_22%),linear-gradient(135deg,rgba(255,255,255,0.98),rgba(243,248,255,0.96))] px-5 py-5 dark:bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.18),transparent_25%),linear-gradient(135deg,rgba(31,41,55,0.98),rgba(17,24,39,0.98))] sm:px-6 sm:py-6">
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-start">
              <div className="min-w-0">
                <div className="flex items-start gap-4">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-card-color-100 text-primary-dui shadow-sm ring-1 ring-slate-200/80 dark:bg-gray-800 dark:ring-slate-600">
                    <FaUser className="text-2xl opacity-90" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-title-page-color-300 dark:text-gray-400">
                      {tPM('timeline.workspaceLabel')}
                    </p>
                    <h1 className="mt-1 truncate text-2xl font-bold tracking-tight text-title-page-color-100 dark:text-white sm:text-3xl">
                      {patientName}
                    </h1>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {patientMeta.map((item) => (
                        <span
                          key={item}
                          className="rounded-full bg-card-color-100/90 px-3 py-1.5 text-xs font-medium text-title-page-color-300 ring-1 ring-slate-200/70 backdrop-blur-sm dark:bg-gray-800/80 dark:text-gray-300 dark:ring-slate-700"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 xl:justify-end">
                {hasWritePermission ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="gap-2 bg-card-color-100/90 dark:bg-gray-800/90"
                    onClick={() => openEditPatientPage(id)}
                  >
                    <FaEdit className="h-4 w-4" />
                    {tPM('timeline.shortcuts.edit')}
                  </Button>
                ) : null}
                {hasWritePermission && patientRecord ? (
                  <Button
                    type="button"
                    size="sm"
                    className="gap-2 px-4"
                    disabled={!canCreateHeaderExam || isCreatingExam}
                    title={
                      !canCreateHeaderExam
                        ? tP('prenatalExam.messages.inactivePregnancy')
                        : undefined
                    }
                    onClick={() => void handleCreateExam()}
                  >
                    {isCreatingExam ? (
                      <FiLoader className="h-4 w-4 animate-spin" />
                    ) : (
                      <FiPlus className="h-4 w-4" />
                    )}
                    {tP('prenatalExam.form.create')}
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        <div className="min-h-0 flex-1 overflow-hidden">
          <PatientDetailsView
            patientId={String(id ?? '')}
            patient={patientRecord ?? null}
            dossier={dossier}
            isDossierLoading={isLoading}
            dossierError={errorPatient}
            pregnancySupplementalContent={pregnancySupplementalContent}
          />
        </div>
      </div>
    </div>
  );
};

export default PatientTimeline;
